import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { DirectUpload } from "@rails/activestorage";

export default class extends Controller {
  static targets = ["textarea", "fileInput", "filenamePill", "filenameText", "submitButton", "errorMessage"];
  static values = { pageNumberField: String };

  textareaTargetConnected() {
    this.validateInput();
  }

  validateInput() {
    const isEmpty = this.textareaTarget.value.trim() === "";
    this.submitButtonTarget.disabled = isEmpty;
    this.submitButtonTarget.classList.toggle("disabled", isEmpty);
  }

  attachFile() {
    this.fileInputTarget.click();
  }

  fileSelected() {
    const file = this.fileInputTarget.files[0];
    if (file) {
      const name = file.name;
      const truncated = name.length > 24 ? name.substring(0, 24) + "..." : name;
      this.filenameTextTarget.textContent = truncated;
      this.filenamePillTarget.classList.remove("hidden");
    }
  }

  removeFile() {
    this.fileInputTarget.value = "";
    this.filenamePillTarget.classList.add("hidden");
  }

  async submit() {
    if (!this.textareaTarget.value.trim()) {
      this.textareaTarget.reportValidity();
      return;
    }

    this.toggleLoading(true);

    const formData = new FormData(this.element);
    const pageNumber = this.getCurrentLabel();
    if (pageNumber !== null && this.hasPageNumberFieldValue) {
      formData.append(this.pageNumberFieldValue, pageNumber);
    }

    const file = this.hasFileInputTarget && this.fileInputTarget.files[0];
    if (file) {
      const fieldName = this.fileInputTarget.name;
      try {
        const blob = await this.directUpload(file, fieldName);
        formData.delete(fieldName);
        formData.append(fieldName, blob.signed_id);
      } catch (error) {
        this.toggleLoading(false);
        alert(`File upload failed: ${error}`);
        return;
      }
    }

    try {
      await this.turboFetch(this.element.action, { method: "POST", body: formData });
      this.textareaTarget.value = "";
      if (this.hasFileInputTarget) {
        this.fileInputTarget.value = "";
      }
      if (this.hasFilenamePillTarget) {
        this.filenamePillTarget.classList.add("hidden");
      }
    } finally {
      this.toggleLoading(false);
      this.validateInput();
    }
  }

  getCurrentLabel() {
    const carouselEl = document.querySelector('[data-controller="gallery-carousel"]');
    if (carouselEl) {
      const controller = this.application.getControllerForElementAndIdentifier(carouselEl, "gallery-carousel");
      return controller ? controller.currentIndex + 1 : null;
    }

    const videoEl = document.querySelector("video");
    if (videoEl) {
      return this.formatVideoTimestamp(videoEl.currentTime);
    }

    return null;
  }

  formatVideoTimestamp(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }

  toggleLoading(loading) {
    const button = this.submitButtonTarget;
    const spinner = button.querySelector("[name='spinner']");
    const icon = button.querySelector("svg:not([name='spinner'])");

    if (!spinner) return;

    spinner.classList.toggle("hidden", !loading);
    icon?.classList.toggle("hidden", loading);
    button.disabled = loading;
  }

  directUpload(file, fieldName) {
    const url = `/rails/active_storage/direct_uploads?attachment=${fieldName}`;
    return new Promise((resolve, reject) => {
      new DirectUpload(file, url).create((error, blob) => {
        if (error) reject(error);
        else resolve(blob);
      });
    });
  }

  async turboFetch(url, options = {}) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");

    const response = await fetch(url, {
      ...options,
      headers: { "X-CSRF-Token": csrfToken, Accept: "text/vnd.turbo-stream.html", ...options.headers },
    });

    if (response.ok) {
      Turbo.renderStreamMessage(await response.text());
    } else {
      this.toggleError(true, "Something went wrong. Please try again.");
    }
  }

  toggleError(hasError, message) {
    this.errorMessageTarget.classList.toggle("hidden", !hasError);

    if (message) {
      this.errorMessageTarget.textContent = message;
    }

    if (hasError) {
      setTimeout(() => this.toggleError(false), 4000);
    }
  }
}
